import React from 'react';
import { Route, Navigate, useParams } from 'react-router-dom';
import ProtectedRoute from '@/modules/auth/ProtectedRoute';
import PortalGatekeeper from '@/modules/common/components/PortalGatekeeper';

const G = ({ children }) => (
  <PortalGatekeeper roleName="student" portalDashboardPath="/alumno/dashboard">
    {children}
  </PortalGatekeeper>
);

// Redirect /contenido to session page with ?tab=material
const ContentRedirect = ({ basePath }) => {
  const { sessionId } = useParams();
  return <Navigate to={`${basePath}/${sessionId}?tab=material`} replace />;
};

// Lazy-loaded tab containers
const MiAulaPage = React.lazy(() => import('@/modules/student/mi-aula/MiAulaPage'));
const RecursosEvalPage = React.lazy(() => import('@/modules/student/recursos-eval/RecursosEvalPage'));
const MiExpedientePage = React.lazy(() => import('@/modules/student/expediente/MiExpedientePage'));
const TramitesAlumnoPage = React.lazy(() => import('@/modules/student/tramites/TramitesAlumnoPage'));
const FinanzasAlumnoPage = React.lazy(() => import('@/modules/student/finanzas/FinanzasAlumnoPage'));
const VidaEstudiantilPage = React.lazy(() => import('@/modules/student/vida-estudiantil/VidaEstudiantilPage'));

// Dashboard & Profile (single pages, no tabs)
const AlumnoDashboard = React.lazy(() => import('@/pages/dashboards/AlumnoDashboard'));
const MiPerfilPage = React.lazy(() => import('@/modules/student/perfil/MiPerfilPage'));

// LMS detail routes (outside tabs)
const StudentTutoringSession = React.lazy(() => import('@/modules/lms/components/student/StudentTutoringSession'));
// SessionContentBrowser is now embedded inside StudentTutoringSession (tab "Material")
const StudentProgressDashboard = React.lazy(() => import('@/modules/lms/components/student/StudentProgressDashboard'));

export const studentRoutes = [
  // Dashboard & Profile (single pages)
  <Route key="/alumno/dashboard" path="/alumno/dashboard" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><AlumnoDashboard /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/perfil" path="/alumno/perfil" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><MiPerfilPage /></G></ProtectedRoute>
  } />,

  // Mi Aula (2 niveles + tabs) — Redesign v2: Mis Programas + Mi Calendario
  <Route key="/alumno/mi-aula" path="/alumno/mi-aula" element={
    <Navigate to="/alumno/mi-aula/mis-programas" replace />
  } />,
  <Route key="/alumno/mi-aula/mis-programas" path="/alumno/mi-aula/mis-programas" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><MiAulaPage /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/mi-aula/mi-calendario" path="/alumno/mi-aula/mi-calendario" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><MiAulaPage /></G></ProtectedRoute>
  } />,
  // Legacy redirects from old Mi Aula tabs
  <Route key="/alumno/mi-aula/programas-activos" path="/alumno/mi-aula/programas-activos" element={<Navigate to="/alumno/mi-aula/mis-programas" replace />} />,
  <Route key="/alumno/mi-aula/plan-desarrollo" path="/alumno/mi-aula/plan-desarrollo" element={<Navigate to="/alumno/mi-aula/mis-programas" replace />} />,
  <Route key="/alumno/mi-aula/tareas-pendientes" path="/alumno/mi-aula/tareas-pendientes" element={<Navigate to="/alumno/mi-aula/mis-programas" replace />} />,
  <Route key="/alumno/mi-aula/clases-en-vivo" path="/alumno/mi-aula/clases-en-vivo" element={<Navigate to="/alumno/mi-aula/mis-programas" replace />} />,

  // Recursos y Evaluación (2 niveles + tabs) — SPLIT from Aula Virtual
  <Route key="/alumno/recursos-eval" path="/alumno/recursos-eval" element={
    <Navigate to="/alumno/recursos-eval/foros" replace />
  } />,
  <Route key="/alumno/recursos-eval/foros" path="/alumno/recursos-eval/foros" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><RecursosEvalPage /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/recursos-eval/grabaciones" path="/alumno/recursos-eval/grabaciones" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><RecursosEvalPage /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/recursos-eval/tutorias" path="/alumno/recursos-eval/tutorias" element={
    <Navigate to="/alumno/mi-aula/mis-programas" replace />
  } />,
  <Route key="/alumno/recursos-eval/evaluacion-docente" path="/alumno/recursos-eval/evaluacion-docente" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><RecursosEvalPage /></G></ProtectedRoute>
  } />,
  // LMS detail routes — viven fuera del tab container
  <Route key="/alumno/recursos-eval/tutorias/sesion/:sessionId" path="/alumno/recursos-eval/tutorias/sesion/:sessionId" element={
    <G><StudentTutoringSession /></G>
  } />,
  <Route key="/alumno/recursos-eval/tutorias/sesion/:sessionId/contenido" path="/alumno/recursos-eval/tutorias/sesion/:sessionId/contenido" element={
    <ContentRedirect basePath="/alumno/recursos-eval/tutorias/sesion" />
  } />,
  <Route key="/alumno/recursos-eval/tutorias/mi-progreso" path="/alumno/recursos-eval/tutorias/mi-progreso" element={
    <G><StudentProgressDashboard /></G>
  } />,

  // Mi Expediente (2 niveles + tabs)
  <Route key="/alumno/expediente" path="/alumno/expediente" element={
    <Navigate to="/alumno/expediente/kardex" replace />
  } />,
  <Route key="/alumno/expediente/kardex" path="/alumno/expediente/kardex" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><MiExpedientePage /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/expediente/calificaciones" path="/alumno/expediente/calificaciones" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><MiExpedientePage /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/expediente/avance-curricular" path="/alumno/expediente/avance-curricular" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><MiExpedientePage /></G></ProtectedRoute>
  } />,

  // Trámites (2 niveles + tabs)
  <Route key="/alumno/tramites" path="/alumno/tramites" element={
    <Navigate to="/alumno/tramites/constancias" replace />
  } />,
  <Route key="/alumno/tramites/constancias" path="/alumno/tramites/constancias" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><TramitesAlumnoPage /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/tramites/becas" path="/alumno/tramites/becas" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><TramitesAlumnoPage /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/tramites/servicio-social" path="/alumno/tramites/servicio-social" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><TramitesAlumnoPage /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/tramites/practicas" path="/alumno/tramites/practicas" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><TramitesAlumnoPage /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/tramites/titulacion" path="/alumno/tramites/titulacion" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><TramitesAlumnoPage /></G></ProtectedRoute>
  } />,

  // Finanzas (2 niveles + tabs)
  <Route key="/alumno/finanzas" path="/alumno/finanzas" element={
    <Navigate to="/alumno/finanzas/estado-cuenta" replace />
  } />,
  <Route key="/alumno/finanzas/inscripcion" path="/alumno/finanzas/inscripcion" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><FinanzasAlumnoPage /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/finanzas/estado-cuenta" path="/alumno/finanzas/estado-cuenta" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><FinanzasAlumnoPage /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/finanzas/pagos-linea" path="/alumno/finanzas/pagos-linea" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><FinanzasAlumnoPage /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/finanzas/facturacion" path="/alumno/finanzas/facturacion" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><FinanzasAlumnoPage /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/finanzas/historial" path="/alumno/finanzas/historial" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><FinanzasAlumnoPage /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/finanzas/convenios-pago" path="/alumno/finanzas/convenios-pago" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><FinanzasAlumnoPage /></G></ProtectedRoute>
  } />,

  // Vida Estudiantil (2 niveles + tabs)
  <Route key="/alumno/vida-estudiantil" path="/alumno/vida-estudiantil" element={
    <Navigate to="/alumno/vida-estudiantil/beneficios" replace />
  } />,
  <Route key="/alumno/vida-estudiantil/beneficios" path="/alumno/vida-estudiantil/beneficios" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><VidaEstudiantilPage /></G></ProtectedRoute>
  } />,
  <Route key="/alumno/vida-estudiantil/bolsa-trabajo" path="/alumno/vida-estudiantil/bolsa-trabajo" element={
    <ProtectedRoute requiredPermission="student.portal.view"><G><VidaEstudiantilPage /></G></ProtectedRoute>
  } />,

  // Legacy redirects from old Aula Virtual paths
  <Route key="/alumno/aula-virtual/programas-activos" path="/alumno/aula-virtual/programas-activos" element={<Navigate to="/alumno/mi-aula/mis-programas" replace />} />,
  <Route key="/alumno/aula-virtual/tareas-pendientes" path="/alumno/aula-virtual/tareas-pendientes" element={<Navigate to="/alumno/mi-aula/mis-programas" replace />} />,
  <Route key="/alumno/aula-virtual/clases-en-vivo" path="/alumno/aula-virtual/clases-en-vivo" element={<Navigate to="/alumno/mi-aula/mis-programas" replace />} />,
  <Route key="/alumno/aula-virtual/foros" path="/alumno/aula-virtual/foros" element={<Navigate to="/alumno/recursos-eval/foros" replace />} />,
  <Route key="/alumno/aula-virtual/grabaciones" path="/alumno/aula-virtual/grabaciones" element={<Navigate to="/alumno/recursos-eval/grabaciones" replace />} />,
  <Route key="/alumno/aula-virtual/tutorias" path="/alumno/aula-virtual/tutorias" element={<Navigate to="/alumno/mi-aula/mis-programas" replace />} />,
  <Route key="/alumno/aula-virtual/tutorias/sesion/:sessionId" path="/alumno/aula-virtual/tutorias/sesion/:sessionId" element={<StudentTutoringSession />} />,
  <Route key="/alumno/aula-virtual/tutorias/sesion/:sessionId/contenido" path="/alumno/aula-virtual/tutorias/sesion/:sessionId/contenido" element={
    <ContentRedirect basePath="/alumno/recursos-eval/tutorias/sesion" />
  } />,
  <Route key="/alumno/aula-virtual/tutorias/mi-progreso" path="/alumno/aula-virtual/tutorias/mi-progreso" element={<StudentProgressDashboard />} />,
  <Route key="/alumno/aula-virtual/evaluacion-docente" path="/alumno/aula-virtual/evaluacion-docente" element={<Navigate to="/alumno/recursos-eval/evaluacion-docente" replace />} />,
];
